import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Button,
  Divider,
  useTheme,
  useMediaQuery,
  Grid
} from '@mui/material';
import { useRealTimeData } from '../hooks/useRealTimeData';
import ConnectionStatus from './ConnectionStatus';
import EmergencyChronometer from './EmergencyChronometer';
import {
  AirportShuttle as AmbulanceIcon,
  Navigation as NavigationIcon,
  LocationOn as LocationIcon,
  Timer as TimerIcon,
  Phone as PhoneIcon,
  LocalHospital as HospitalIcon,
  Person as PersonIcon,
  Speed as SpeedIcon
} from '@mui/icons-material';

interface AmbulanceStatus {
  id: string;
  status: 'dispatched' | 'en_route_to_patient' | 'with_patient' | 'en_route_to_hospital' | 'arrived_hospital';
  currentLocation: string;
  estimatedArrival: string;
  distance: string;
  speed: string;
  crew: {
    paramedic: string;
    driver: string;
  };
  emergencyStartTime: string;
}

interface AmbulanceTrackerProps {
  emergencyId?: string;
  patientName?: string;
  isActive?: boolean;
}

const AmbulanceTracker: React.FC<AmbulanceTrackerProps> = ({
  emergencyId = 'EMG001',
  patientName = 'Juan Pérez',
  isActive = true
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Use real-time data hook for ambulance status
  const { 
    data: ambulanceData, 
    loading: ambulanceLoading, 
    error: ambulanceError,
    lastUpdate
  } = useRealTimeData<any>(`ambulance-AMB-07`, undefined, async () => {
    // Fallback fetch function (would be real API call in production)
    return {
      id: 'AMB-07',
      status: 'en_route_to_patient',
      location: 'Av. Brasil con O\'Higgins',
      eta: '6 minutos',
      distance: '2.3 km',
      speed: '45 km/h',
      lastUpdate: Date.now()
    };
  });

  // Default ambulance status with real-time data overlay
  const ambulanceStatus: AmbulanceStatus = {
    id: ambulanceData?.id || 'AMB-07',
    status: ambulanceData?.status || 'en_route_to_patient',
    currentLocation: ambulanceData?.location || 'Av. Brasil con O\'Higgins',
    estimatedArrival: ambulanceData?.eta || '6 minutos',
    distance: ambulanceData?.distance || '2.3 km',
    speed: ambulanceData?.speed || '45 km/h',
    crew: {
      paramedic: 'Carlos Mendoza',
      driver: 'Ana Rojas'
    },
    emergencyStartTime: new Date(Date.now() - 8 * 60 * 1000).toISOString()
  };

  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'dispatched':
        return { label: 'DESPACHADO', color: 'info', progress: 20 };
      case 'en_route_to_patient':
        return { label: 'EN RUTA A SU UBICACIÓN', color: 'warning', progress: 50 };
      case 'with_patient':
        return { label: 'CON EL PACIENTE', color: 'primary', progress: 70 };
      case 'en_route_to_hospital':
        return { label: 'EN RUTA AL HOSPITAL', color: 'warning', progress: 90 };
      case 'arrived_hospital':
        return { label: 'LLEGÓ AL HOSPITAL', color: 'success', progress: 100 };
      default:
        return { label: 'DESCONOCIDO', color: 'default', progress: 0 };
    }
  };

  const statusInfo = getStatusInfo(ambulanceStatus.status);

  if (!isActive) {
    return (
      <Card sx={{ bgcolor: 'grey.100' }}>
        <CardContent sx={{ textAlign: 'center', py: 3 }}>
          <AmbulanceIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
          <Typography variant="h6" color="grey.600">
            No hay emergencias activas
          </Typography>
          <Typography variant="body2" color="grey.500">
            La ambulancia estará disponible en caso de emergencia
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ border: '2px solid #f44336', boxShadow: 3 }}>
      <CardContent sx={{ p: isSmallMobile ? 2 : 3 }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: isSmallMobile ? 'flex-start' : 'center', 
          mb: 2,
          flexDirection: isSmallMobile ? 'column' : 'row',
          textAlign: isSmallMobile ? 'center' : 'left'
        }}>
          <Avatar sx={{ 
            bgcolor: 'error.main', 
            mr: isSmallMobile ? 0 : 2,
            mb: isSmallMobile ? 1 : 0,
            width: isSmallMobile ? 48 : 56,
            height: isSmallMobile ? 48 : 56
          }}>
            <AmbulanceIcon fontSize={isSmallMobile ? 'medium' : 'large'} />
          </Avatar>
          <Box sx={{ flexGrow: 1, mb: isSmallMobile ? 1 : 0 }}>
            <Typography 
              variant={isSmallMobile ? 'h6' : 'h6'} 
              fontWeight="bold"
              sx={{ fontSize: isSmallMobile ? '1.1rem' : undefined }}
            >
              Ambulancia {ambulanceStatus.id}
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ fontSize: isSmallMobile ? '0.85rem' : undefined }}
            >
              Para {patientName} • Emergencia {emergencyId}
            </Typography>
          </Box>
          <Chip 
            label={statusInfo.label} 
            color={statusInfo.color as any} 
            size={isSmallMobile ? 'small' : 'medium'}
            sx={{ 
              fontWeight: 'bold',
              fontSize: isSmallMobile ? '0.75rem' : undefined
            }}
          />
        </Box>

        {/* Emergency Chronometer */}
        <Box sx={{ mb: isSmallMobile ? 2 : 3 }}>
          <EmergencyChronometer 
            startTime={ambulanceStatus.emergencyStartTime}
            variant="detailed"
            size={isSmallMobile ? "small" : "medium"}
          />
        </Box>

        {/* Progress Bar */}
        <Box sx={{ mb: isSmallMobile ? 2 : 3 }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: 1,
            flexDirection: isSmallMobile ? 'column' : 'row',
            textAlign: isSmallMobile ? 'center' : 'left'
          }}>
            <Typography 
              variant="body2" 
              fontWeight="bold"
              sx={{ 
                fontSize: isSmallMobile ? '0.9rem' : undefined,
                mb: isSmallMobile ? 0.5 : 0
              }}
            >
              Progreso de la Emergencia
            </Typography>
            <Typography 
              variant="body2"
              sx={{ fontSize: isSmallMobile ? '0.85rem' : undefined }}
            >
              {statusInfo.progress}%
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={statusInfo.progress}
            color={statusInfo.color === 'success' ? 'success' : 'primary'}
            sx={{ height: isSmallMobile ? 6 : 8, borderRadius: 4 }}
          />
        </Box>

        {/* Current Status Details */}
        <Alert 
          severity={statusInfo.color === 'success' ? 'success' : 'warning'} 
          sx={{ mb: 2 }}
        >
          <Typography 
            variant="subtitle2" 
            fontWeight="bold"
            sx={{ 
              fontSize: isSmallMobile ? '0.85rem' : undefined,
              textAlign: isSmallMobile ? 'center' : 'left'
            }}
          >
            {ambulanceStatus.status === 'en_route_to_patient' && (
              <>🚑 La ambulancia está en camino hacia su ubicación</>
            )}
            {ambulanceStatus.status === 'with_patient' && (
              <>🩺 Los paramédicos están atendiendo al paciente</>
            )}
            {ambulanceStatus.status === 'en_route_to_hospital' && (
              <>🏥 Transportando al paciente al hospital</>
            )}
            {ambulanceStatus.status === 'arrived_hospital' && (
              <>✅ El paciente ha llegado al hospital y está siendo atendido</>
            )}
          </Typography>
        </Alert>

        {/* Location & ETA Info */}
        {isMobile ? (
          <Grid container spacing={1} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                <LocationIcon color="primary" sx={{ mb: 0.5 }} />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Ubicación
                </Typography>
                <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem' }}>
                  {ambulanceStatus.currentLocation}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                <TimerIcon color="warning" sx={{ mb: 0.5 }} />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  ETA
                </Typography>
                <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem' }}>
                  {ambulanceStatus.estimatedArrival}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                <NavigationIcon color="info" sx={{ mb: 0.5 }} />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Distancia
                </Typography>
                <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem' }}>
                  {ambulanceStatus.distance}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                <SpeedIcon color="success" sx={{ mb: 0.5 }} />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Velocidad
                </Typography>
                <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem' }}>
                  {ambulanceStatus.speed}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        ) : (
          <List dense>
            <ListItem>
              <ListItemIcon>
                <LocationIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Ubicación Actual"
                secondary={ambulanceStatus.currentLocation}
              />
            </ListItem>
            
            <ListItem>
              <ListItemIcon>
                <TimerIcon color="warning" />
              </ListItemIcon>
              <ListItemText 
                primary="Tiempo Estimado de Llegada"
                secondary={ambulanceStatus.estimatedArrival}
              />
            </ListItem>
            
            <ListItem>
              <ListItemIcon>
                <NavigationIcon color="info" />
              </ListItemIcon>
              <ListItemText 
                primary="Distancia Restante"
                secondary={ambulanceStatus.distance}
              />
            </ListItem>
            
            <ListItem>
              <ListItemIcon>
                <SpeedIcon color="success" />
              </ListItemIcon>
              <ListItemText 
                primary="Velocidad Actual"
                secondary={ambulanceStatus.speed}
              />
            </ListItem>
          </List>
        )}

        <Divider sx={{ my: isSmallMobile ? 1.5 : 2 }} />

        {/* Crew Information */}
        <Typography 
          variant="subtitle2" 
          gutterBottom 
          fontWeight="bold"
          sx={{ 
            fontSize: isSmallMobile ? '0.9rem' : undefined,
            textAlign: isSmallMobile ? 'center' : 'left'
          }}
        >
          Equipo de Ambulancia:
        </Typography>
        
        {isMobile ? (
          <Grid container spacing={1} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                <PersonIcon sx={{ mb: 0.5, color: 'grey.600' }} />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Paramédico
                </Typography>
                <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem' }}>
                  {ambulanceStatus.crew.paramedic}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                <PersonIcon sx={{ mb: 0.5, color: 'grey.600' }} />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Conductor
                </Typography>
                <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem' }}>
                  {ambulanceStatus.crew.driver}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        ) : (
          <List dense>
            <ListItem sx={{ py: 0 }}>
              <ListItemIcon>
                <PersonIcon />
              </ListItemIcon>
              <ListItemText 
                primary="Paramédico"
                secondary={ambulanceStatus.crew.paramedic}
              />
            </ListItem>
            <ListItem sx={{ py: 0 }}>
              <ListItemIcon>
                <PersonIcon />
              </ListItemIcon>
              <ListItemText 
                primary="Conductor"
                secondary={ambulanceStatus.crew.driver}
              />
            </ListItem>
          </List>
        )}

        {/* Action Buttons */}
        <Box sx={{ 
          display: 'flex', 
          gap: isSmallMobile ? 1 : 1, 
          mt: 2,
          flexDirection: isSmallMobile ? 'column' : 'row'
        }}>
          <Button
            variant="outlined"
            startIcon={<PhoneIcon />}
            fullWidth
            size={isSmallMobile ? 'large' : 'medium'}
            onClick={() => window.open('tel:131')}
            sx={{ 
              py: isSmallMobile ? 1.5 : undefined,
              fontSize: isSmallMobile ? '1rem' : undefined
            }}
          >
            Llamar Ambulancia
          </Button>
          <Button
            variant="outlined"
            startIcon={<HospitalIcon />}
            fullWidth
            size={isSmallMobile ? 'large' : 'medium'}
            onClick={() => window.open('tel:+56412345678')}
            sx={{ 
              py: isSmallMobile ? 1.5 : undefined,
              fontSize: isSmallMobile ? '1rem' : undefined
            }}
          >
            Llamar Hospital
          </Button>
        </Box>

        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mt: 2,
          flexDirection: isSmallMobile ? 'column' : 'row',
          gap: isSmallMobile ? 1 : 0
        }}>
          <Typography 
            variant="caption" 
            color="text.secondary" 
            sx={{ 
              fontSize: isSmallMobile ? '0.7rem' : undefined,
              textAlign: isSmallMobile ? 'center' : 'left'
            }}
          >
            Información actualizada en tiempo real
          </Typography>
          <ConnectionStatus variant="icon" size="small" />
        </Box>
      </CardContent>
    </Card>
  );
};

export default AmbulanceTracker;